import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { PageSkeleton } from "@/components/PageSkeleton";
import { Shell } from "@/components/Shell";
import { SignIn } from "@/features/session/SignIn";
import { useMe } from "@/features/session/useMe";
import { kiesSeizoen, useSeizoenen } from "@/features/session/useSeizoenen";
import { Wedstrijdboekjes } from "@/features/wedstrijdboekjes/Wedstrijdboekjes";
import { keepScrollPosition } from "@jbnl/shared-ui";

export function WedstrijdboekjesPage() {
  const { t } = useTranslation(["paginas", "navigatie", "common"]);
  const { data: me, isPending } = useMe();
  const { data: seizoenen } = useSeizoenen();
  const { seizoen: seizoenId, boekje: boekjeId } = useSearch({
    from: "/wedstrijdboekjes",
  });
  const navigate = useNavigate({ from: "/wedstrijdboekjes" });

  if (isPending) {
    return (
      <Shell>
        <PageSkeleton />
      </Shell>
    );
  }

  if (!me) {
    return (
      <Shell>
        <SignIn />
      </Shell>
    );
  }

  if (!me.isBeheerder) {
    return (
      <Shell name={me.name} letterhead>
        <Card>
          <CardHeader>
            <CardTitle>{t("common:doors.noAccess")}</CardTitle>
            <CardDescription>
              {t("common:doors.noAccessDescription")}
            </CardDescription>
          </CardHeader>
        </Card>
      </Shell>
    );
  }

  // A seizoen named in the URL wins; otherwise the one last picked on the Seizoenen screen;
  // otherwise the running one. An id that no longer exists is a page with nothing chosen, so it
  // falls back the same way rather than answering not-found.
  const seizoen = kiesSeizoen(seizoenen, seizoenId);

  return (
    <Shell name={me.name} wide>
      <header className="mb-6 space-y-1">
        <h1 className="text-2xl font-bold tracking-[-0.02em]">
          {t("navigatie:paginas.wedstrijdboekjes.label")}
        </h1>
        {seizoen ? (
          <p className="text-sm text-muted-foreground">
            {t("common:seizoen.lopend", { naam: seizoen.name })}{" "}
            {t("wedstrijdboekjes.uitleg")}
            {t("common:seizoen.andereBefore")}
            <Link className="jb-link" to="/seizoenen">
              {t("common:seizoen.link")}
            </Link>
            {t("common:seizoen.na")}
          </p>
        ) : (
          <p className="text-sm text-muted-foreground">
            {t("common:seizoen.geenBefore")}
            <Link className="jb-link" to="/seizoenen">
              {t("common:seizoen.link")}
            </Link>
            {t("common:seizoen.na")}
          </p>
        )}
      </header>

      {seizoen && (
        <Wedstrijdboekjes
          seizoen={seizoen}
          boekjeId={boekjeId}
          onKies={(id) =>
            void navigate({
              ...keepScrollPosition,
              search: { seizoen: seizoen.id, boekje: id },
            })
          }
        />
      )}
    </Shell>
  );
}
